export type SoundName = 'cafe' | 'thunder' | 'waves' | 'fire' | 'birds' | 'rain' | 'wind' | 'river' | 'whitenoise' | 'night' | 'train' | 'library'
export type StationName = 'lofi' | 'synthwave' | 'piano' | 'focus'

/** Each station is a rotating playlist that plays like a little radio. */
const STATION_TRACKS: Record<StationName, string[]> = {
  lofi: ['/music/lofi-1.mp3', '/music/lofi-2.mp3', '/music/lofi-3.mp3'],
  synthwave: ['/music/synthwave-1.mp3', '/music/synthwave-2.mp3', '/music/synthwave-3.mp3'],
  piano: ['/music/piano-1.mp3', '/music/piano-2.mp3', '/music/piano-3.mp3'],
  focus: ['/music/focus-1.mp3', '/music/focus-2.mp3', '/music/focus-3.mp3'],
}

export class SoundEngine {
  private readonly active = new Map<SoundName, HTMLAudioElement>()
  private radio: { station: StationName; audio: HTMLAudioElement; index: number } | null = null
  private volume = 0.45

  setVolume(value: number): void {
    this.volume = Math.max(0, Math.min(1, value))
    this.active.forEach((audio) => { audio.volume = this.volume })
    if (this.radio) this.radio.audio.volume = this.volume
  }

  activeStation(): StationName | null {
    return this.radio?.station ?? null
  }

  /** Returns true when the station is now playing, false when it was turned off. */
  async toggleStation(name: StationName): Promise<boolean> {
    if (this.radio?.station === name) {
      this.stopStation()
      return false
    }
    this.stopStation()
    const tracks = STATION_TRACKS[name]
    const index = Math.floor(Math.random() * tracks.length)
    return this.playStationTrack(name, index)
  }

  private async playStationTrack(name: StationName, index: number): Promise<boolean> {
    const tracks = STATION_TRACKS[name]
    const audio = new Audio(tracks[index % tracks.length])
    audio.preload = 'auto'
    audio.volume = this.volume
    audio.addEventListener('ended', () => {
      if (this.radio?.audio === audio) void this.playStationTrack(name, index + 1)
    })
    this.radio = { station: name, audio, index }
    try {
      await audio.play()
      return true
    } catch {
      if (this.radio?.audio === audio) this.stopStation()
      return false
    }
  }

  stopStation(): void {
    if (!this.radio) return
    this.radio.audio.pause()
    this.radio.audio.src = ''
    this.radio = null
  }

  getVolume(): number {
    return this.volume
  }

  isActive(name: SoundName): boolean {
    return this.active.has(name)
  }

  async toggle(name: SoundName): Promise<boolean> {
    if (this.active.has(name)) {
      this.stop(name)
      return false
    }
    return this.start(name)
  }

  private async start(name: SoundName): Promise<boolean> {
    const audio = new Audio(`/sounds/${name}.mp3`)
    audio.loop = true
    audio.preload = 'auto'
    audio.volume = this.volume
    this.active.set(name, audio)
    try {
      await audio.play()
      return true
    } catch {
      this.stop(name)
      return false
    }
  }

  stop(name: SoundName): void {
    const audio = this.active.get(name)
    if (audio) {
      audio.pause()
      audio.currentTime = 0
      audio.src = ''
    }
    this.active.delete(name)
  }

  stopAll(): void {
    ;[...this.active.keys()].forEach((name) => this.stop(name))
    this.stopStation()
  }

  activeNames(): SoundName[] {
    return [...this.active.keys()]
  }
}

/** One engine for the whole app: ambient sounds keep playing when panels close. */
export const soundEngine = new SoundEngine()
